import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';

export default function Dashboard() {
  const [stats, setStats] = useState({
    apps: 0,
    webhooks: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();

  useEffect(() => {
    fetchDashboardData();
  }, [activeCompanyId]);

  const fetchDashboardData = async () => {
    try {
      const appsRes = await api.get('/api/apps');

      setStats({
        apps: appsRes.data.length || 0,
        webhooks: 0 // Update when webhook endpoint is ready
      });
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold">Developer Dashboard</h1>
      <p className="text-sm sm:text-base text-gray-700">
        Manage your integrations, API keys, webhooks, and submit apps to Dentulu Marketplace.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <h3 className="text-base sm:text-lg font-semibold text-gray-600">My Apps</h3>
          <p className="text-2xl sm:text-3xl font-bold mt-2">{stats.apps}</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Registered applications</p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <h3 className="text-base sm:text-lg font-semibold text-gray-600">Webhooks</h3>
          <p className="text-2xl sm:text-3xl font-bold mt-2">{stats.webhooks}</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Active webhooks</p>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div
            onClick={() => navigate('/dashboard/apps')}
            className="block p-3 sm:p-4 border rounded hover:bg-gray-50 transition cursor-pointer"
          >
            <h3 className="font-semibold text-sm sm:text-base">📱 Manage Apps</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">View and manage your registered applications</p>
          </div>
          <div
            onClick={() => navigate('/dashboard/webhooks')}
            className="block p-3 sm:p-4 border rounded hover:bg-gray-50 transition cursor-pointer"
          >
            <h3 className="font-semibold text-sm sm:text-base">🔔 Webhooks</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Configure webhook endpoints</p>
          </div>
          <Link
            to="/"
            className="block p-3 sm:p-4 border rounded hover:bg-gray-50 transition"
          >
            <h3 className="font-semibold text-sm sm:text-base flex items-center gap-2">
              <Package className="w-5 h-5" /> Visit Marketplace
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Browse and discover apps in the marketplace</p>
          </Link>
          {/* <Link
            to="/dashboard/my-purchases"
            className={`flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg ${
              location.pathname === '/dashboard/my-purchases' ? 'bg-gray-100' : ''
            }`}
          >
            <ShoppingBag className="w-5 h-5" />
            My Purchases
          </Link> */}
          <div
            onClick={() => navigate('/dashboard/submit-app')}
            className="block p-3 sm:p-4 border rounded hover:bg-gray-50 transition cursor-pointer"
          >
            <h3 className="font-semibold text-sm sm:text-base">🚀 Submit App</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Submit a new app to the marketplace</p>
          </div>
        </div>
      </div>

      {/* API Documentation section - Swagger disabled
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
          🔌 API Documentation
        </h2>
        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
          Full API reference with example payloads, authentication and testing tools.
        </p>
        <a
          href={process.env.REACT_APP_API_DOCS_URL || `${process.env.REACT_APP_API_BASE_URL}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline flex items-center gap-1 text-sm sm:text-base"
        >
          Open Swagger UI ↗
        </a>
      </div>
      */}
    </div>
  );
}